import { useTitle } from "react-use";
import { Text, Title } from "@mantine/core";

/** Every page title follows "Название | Лекторий ФПМИ" — set just the name. */
export function usePageTitle(title: string) {
  useTitle(`${title} | Лекторий ФПМИ`);
}

export function ErrorDisplay(props: {
  error: Error,
}) {
  return <Text c="dimmed">{props.error.message}</Text>
}

export function Loading() {
  return null;
}

export function NotFoundPage() {
  usePageTitle("Страница не найдена");
  return <Title order={3}>Not Found</Title>
}

const pad = (n: number) => String(n).padStart(2, "0");

function asDate(value: Date | string): Date {
  if (typeof value !== "string") return value;
  // A bare "YYYY-MM-DD" is parsed as UTC midnight, which renders as the day
  // before in any negative offset; read those as a local calendar date.
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  return new Date(value);
}

/** Format a date as "09.02.2026" (local time). */
export function formatDate(value: Date | string): string {
  const d = asDate(value);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Format a date as "09.02.2026 15:30:32" (local time). */
export function formatDateTime(value: Date | string): string {
  const d = asDate(value);
  return `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Reduce a handle/URL to its bare id: split on "/" and "@", keep the last segment (mirrors the backend). */
export function normalizeHandle(value: string): string {
  const parts = value.split(/[/@]/).map(s => s.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "";
}

const HANDLE_OK = /^[A-Za-z0-9._-]+$/;

/** A warning message if a handle still has characters that don't belong in a username, else null. */
export function handleWarning(value: string | null | undefined): string | null {
  if (!value) return null;
  const norm = normalizeHandle(value);
  if (norm && !HANDLE_OK.test(norm)) {
    return `Возможно, некорректный идентификатор: «${norm}»`;
  }
  return null;
}

/** Возвращает 'Фамилия Имя' из переданного объекта */
export function formatFullName(
  s?: { last_name?: string | null; first_name?: string | null; middle_name?: string | null } | null,
  withMiddle = false
): string {
  if (!s) return "";
  const parts = [s.last_name, s.first_name, withMiddle ? s.middle_name : null];
  return parts.filter(Boolean).join(" ");
}

/** Возвращает логин без @phystech.edu */
export function extractLogin(email?: string | null): string {
  return email ? email.split("@")[0] : "";
}
